import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Save, ChevronDown, ChevronUp, FileEdit, RotateCcw, Wand2, Loader2, Image as ImageIcon, Eye } from "lucide-react";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";
import { COMICS, buildPagePrompt, deriveScenes } from "@/data/comics";
import { Button } from "@/components/ui/button";
import ComicReader from "@/pages/ComicReader";
import { SellerPreviewModal } from "@/components/preview/SellerPreviewModal";

const Field = ({ label, value, onChange, rows = 2, testid }) => (
  <label className="block">
    <div className="text-[10px] font-display font-black uppercase tracking-widest text-neutral-600 mb-0.5">
      {label}
    </div>
    <textarea
      data-testid={testid}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className="w-full brutal-sm bg-white p-2 text-xs sm:text-sm font-body resize-y"
    />
  </label>
);

const AIRegenPanel = ({ comic, pageIndex, sceneIndex, defaultPrompt, illustration, onRefresh, lang }) => {
  const [prompt, setPrompt] = useState(defaultPrompt || "");
  const [busy, setBusy] = useState(false);

  const doGenerate = async (force) => {
    if (!prompt.trim()) {
      toast.error(lang === "es" ? "Escribe un prompt" : "Write a prompt");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/comics/illustration", {
        slug: comic.slug,
        page_index: pageIndex,
        scene_index: sceneIndex || 0,
        prompt,
        force,
      });
      toast.success(lang === "es" ? "Imagen lista" : "Image ready");
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="brutal-sm p-2 bg-canales-cream mt-2" data-testid={`ai-regen-${comic.slug}-${pageIndex}-${sceneIndex}`}>
      <div className="flex items-start gap-2">
        <div className="w-16 h-16 flex-shrink-0 border-2 border-black rounded bg-white overflow-hidden relative">
          {illustration ? (
            <img
              src={`data:${illustration.mime_type};base64,${illustration.data}`}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="w-5 h-5 text-neutral-400" />
            </div>
          )}
          {busy && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <Loader2 className="w-4 h-4 text-white animate-spin" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-display font-black uppercase tracking-widest text-canales-blue mb-1">
            {sceneIndex === 0
              ? (lang === "es" ? "Imagen hero" : "Hero image")
              : `${lang === "es" ? "Escena" : "Scene"} ${sceneIndex}`}
          </div>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={2}
            className="w-full brutal-sm bg-white p-1.5 text-[11px] font-body resize-y"
            data-testid={`ai-prompt-${comic.slug}-${pageIndex}-${sceneIndex}`}
          />
          <div className="mt-1 flex gap-1">
            <Button
              onClick={() => doGenerate(false)}
              disabled={busy || !!illustration}
              data-testid={`ai-gen-${comic.slug}-${pageIndex}-${sceneIndex}`}
              className="brutal-btn bg-canales-blue text-white px-2 py-0.5 text-[10px]"
            >
              <Wand2 className="w-3 h-3 mr-1" /> Generar
            </Button>
            <Button
              onClick={() => doGenerate(true)}
              disabled={busy || !illustration}
              data-testid={`ai-regen-btn-${comic.slug}-${pageIndex}-${sceneIndex}`}
              className="brutal-btn bg-canales-yellow text-black px-2 py-0.5 text-[10px]"
            >
              <RotateCcw className="w-3 h-3 mr-1" /> Regenerar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PageEditor = ({ comic, page, index, override, onChange, lang, illMap, refreshIllus }) => {
  const [open, setOpen] = useState(index <= 1);
  const setField = (path, value) => {
    const next = { ...(override || {}) };
    // path is dotted like "title_es" or "panels.0.text_es" or "scenes.1.caption_es"
    const parts = path.split(".");
    let cur = next;
    for (let i = 0; i < parts.length - 1; i++) {
      const key = parts[i];
      if (!(key in cur)) cur[key] = /^\d+$/.test(parts[i + 1]) ? [] : {};
      cur = cur[key];
    }
    cur[parts[parts.length - 1]] = value;
    onChange(next);
  };

  const merged = (fallback, ovKey) => (override && override[ovKey] != null ? override[ovKey] : fallback);
  const mergedPanel = (i, key, fallback) =>
    override?.panels?.[i]?.[key] != null ? override.panels[i][key] : fallback;
  const mergedScene = (i, key, fallback) =>
    override?.scenes?.[i]?.[key] != null ? override.scenes[i][key] : fallback;

  const scenes = Array.isArray(page.scenes) ? page.scenes : [];

  return (
    <div className="brutal p-3 bg-white" data-testid={`comic-content-page-${comic.slug}-${index}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between"
        data-testid={`comic-content-toggle-${comic.slug}-${index}`}
      >
        <div className="text-left">
          <div className="text-[10px] font-display font-black uppercase tracking-widest text-canales-red">
            p{index + 1} · {page.layout || "panels"}
          </div>
          <div className="font-display font-bold text-sm">
            {merged(page.title?.es, "title_es") || (index === 0 ? "Portada" : `Página ${index + 1}`)}
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {page.layout !== "cover" && (
            <div className="grid sm:grid-cols-2 gap-2">
              <Field
                label="Título (ES)"
                value={merged(page.title?.es, "title_es")}
                onChange={(v) => setField("title_es", v)}
                testid={`comic-content-title-es-${index}`}
              />
              <Field
                label="Título (EN)"
                value={merged(page.title?.en, "title_en")}
                onChange={(v) => setField("title_en", v)}
                testid={`comic-content-title-en-${index}`}
              />
            </div>
          )}
          {page.caption && (
            <div className="grid sm:grid-cols-2 gap-2">
              <Field
                label="Caption narrador (ES)"
                value={merged(page.caption.es, "caption_es")}
                onChange={(v) => setField("caption_es", v)}
                rows={3}
                testid={`comic-content-caption-es-${index}`}
              />
              <Field
                label="Caption narrator (EN)"
                value={merged(page.caption.en, "caption_en")}
                onChange={(v) => setField("caption_en", v)}
                rows={3}
                testid={`comic-content-caption-en-${index}`}
              />
            </div>
          )}
          {(page.panels || []).map((p, i) => (
            <div key={i} className="brutal-sm p-2 bg-canales-cream">
              <div className="text-[10px] font-display font-black uppercase tracking-widest text-canales-blue mb-1">
                Panel {i + 1} · {p.speaker || "narrator"}
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <Field
                  label="Texto (ES)"
                  value={mergedPanel(i, "text_es", p.text?.es)}
                  onChange={(v) => setField(`panels.${i}.text_es`, v)}
                  testid={`comic-content-panel-${i}-es-${index}`}
                />
                <Field
                  label="Text (EN)"
                  value={mergedPanel(i, "text_en", p.text?.en)}
                  onChange={(v) => setField(`panels.${i}.text_en`, v)}
                  testid={`comic-content-panel-${i}-en-${index}`}
                />
              </div>
            </div>
          ))}
          {scenes.length > 0 && (
            <div className="brutal-sm p-2 bg-yellow-50">
              <div className="text-[10px] font-display font-black uppercase tracking-widest text-canales-red mb-1">
                {scenes.length} escenas
              </div>
              {scenes.map((sc, i) => (
                <div key={i} className="mb-2 border-t-2 border-black/20 pt-2 first:border-t-0 first:pt-0">
                  <div className="text-[10px] font-display font-bold mb-1">Escena {i + 1}</div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <Field
                      label="Caption (ES)"
                      value={mergedScene(i, "caption_es", sc.caption?.es)}
                      onChange={(v) => setField(`scenes.${i}.caption_es`, v)}
                    />
                    <Field
                      label="Caption (EN)"
                      value={mergedScene(i, "caption_en", sc.caption?.en)}
                      onChange={(v) => setField(`scenes.${i}.caption_en`, v)}
                    />
                  </div>
                  <AIRegenPanel
                    comic={comic}
                    pageIndex={index}
                    sceneIndex={i + 1}
                    defaultPrompt={sc.prompt}
                    illustration={illMap?.[i + 1]}
                    onRefresh={refreshIllus}
                    lang={lang}
                  />
                </div>
              ))}
            </div>
          )}

          {/* AI hero regen (always available) */}
          <AIRegenPanel
            comic={comic}
            pageIndex={index}
            sceneIndex={0}
            defaultPrompt={buildPagePrompt(comic, page, index)}
            illustration={illMap?.[0]}
            onRefresh={refreshIllus}
            lang={lang}
          />

          {/* Auto-derived scenes preview (when page has no explicit scenes but 2+ panels) */}
          {scenes.length === 0 && (page.panels?.length || 0) >= 2 && (
            <div className="brutal-sm p-2 bg-blue-50">
              <div className="text-[10px] font-display font-black uppercase tracking-widest text-canales-blue mb-1">
                {lang === "es" ? "Escenas auto-derivadas" : "Auto-derived scenes"}
              </div>
              {deriveScenes(comic, page, index).map((sc, i) => (
                <AIRegenPanel
                  key={i}
                  comic={comic}
                  pageIndex={index}
                  sceneIndex={i + 1}
                  defaultPrompt={sc.prompt}
                  illustration={illMap?.[i + 1]}
                  onRefresh={refreshIllus}
                  lang={lang}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function ComicContentEditor() {
  const { lang } = useLang();
  const [selected, setSelected] = useState(COMICS[0].slug);
  const [overrides, setOverrides] = useState({});
  const [illMap, setIllMap] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const comic = useMemo(() => COMICS.find((c) => c.slug === selected), [selected]);

  const loadOverrides = async (slug) => {
    try {
      const { data } = await api.get(`/comics/${slug}/content`);
      setOverrides(data?.overrides || {});
      setDirty(false);
    } catch {
      setOverrides({});
    }
  };
  const loadIllus = async (slug) => {
    try {
      const { data } = await api.get(`/comics/${slug}/illustrations`);
      const m = {};
      for (const it of data) {
        const p = it.page_index || 0;
        const s = it.scene_index || 0;
        if (!m[p]) m[p] = {};
        m[p][s] = it;
      }
      setIllMap(m);
    } catch {
      setIllMap({});
    }
  };
  useEffect(() => {
    loadOverrides(selected);
    loadIllus(selected);
  }, [selected]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/comics/${selected}/content`, { slug: selected, overrides });
      toast.success(lang === "es" ? "Guardado" : "Saved");
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    if (!window.confirm(lang === "es" ? "¿Descartar cambios sin guardar?" : "Discard unsaved changes?")) return;
    loadOverrides(selected);
  };

  const setPageOverride = (pageIndex, next) => {
    setOverrides((o) => ({ ...o, [pageIndex]: next }));
    setDirty(true);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="admin-comic-content">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display font-black text-4xl flex items-center gap-3">
          <FileEdit className="w-8 h-8 text-canales-red" />
          {lang === "es" ? "Editar historia del cómic" : "Edit comic content"}
        </h1>
        <div className="flex gap-2">
          <Button
            onClick={() => setPreviewOpen(true)}
            data-testid="comic-content-preview"
            className="brutal-btn bg-canales-blue text-white px-3 py-2 text-sm"
          >
            <Eye className="w-4 h-4 mr-1" />
            {lang === "es" ? "Vista previa" : "Preview"}
          </Button>
          <Button
            onClick={reset}
            disabled={!dirty || saving}
            className="brutal-btn bg-white text-black px-3 py-2 text-sm"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            {lang === "es" ? "Descartar" : "Discard"}
          </Button>
          <Button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="comic-content-save"
            className="brutal-btn bg-canales-red text-white px-4 py-2"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? (lang === "es" ? "Guardando…" : "Saving…") : (lang === "es" ? "Guardar" : "Save")}
          </Button>
        </div>
      </div>

      <p className="text-sm font-body text-neutral-600">
        {lang === "es"
          ? "Cambia títulos, captions y diálogos sin tocar código. Se guardan en Mongo y el reader los aplica al instante."
          : "Edit titles, captions and dialogues without touching code. Stored in Mongo, applied instantly in the reader."}
      </p>

      <div className="flex flex-wrap gap-2" data-testid="comic-content-selector">
        {COMICS.map((c) => (
          <button
            key={c.slug}
            onClick={() => setSelected(c.slug)}
            data-testid={`comic-content-tab-${c.slug}`}
            className={`brutal-btn px-3 py-1.5 text-sm font-display font-bold ${
              selected === c.slug ? "bg-canales-red text-white" : "bg-white text-black"
            }`}
          >
            {c.title[lang]}
          </button>
        ))}
      </div>

      {comic && (
        <div className="space-y-3">
          {comic.pages.map((p, i) => (
            <PageEditor
              key={i}
              comic={comic}
              page={p}
              index={i}
              override={overrides[i]}
              onChange={(next) => setPageOverride(i, next)}
              lang={lang}
            />
          ))}
        </div>
      )}

      <SellerPreviewModal open={previewOpen} onOpenChange={setPreviewOpen}>
        {previewOpen && (
          <ComicReader embedded previewSlug={selected} previewOverrides={overrides} />
        )}
      </SellerPreviewModal>
    </motion.div>
  );
}
